import {useState} from 'react'
import {FlaskConical} from 'lucide-react'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {Button} from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {EntityLink} from '@/components/shared/EntityLink'
import {IconText} from '@/components/shared/IconText'
import {MutedText} from '@/components/shared/MutedText'
import {Pagination} from '@/components/ui/pagination'
import {useI18n} from '@/i18n'
import type {RestrictionRuleTestResult, RestrictionRuleTestResultItem} from '@/types/restriction-rule'

interface TestResultsPanelProps {
    result: RestrictionRuleTestResult | undefined
    isLoading: boolean
    isError: boolean
}

const PAGE_SIZE = 20

export function TestResultsPanel({result, isLoading, isError}: TestResultsPanelProps) {
    const i18n = useI18n()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dialogPage, setDialogPage] = useState(0)

    const items: RestrictionRuleTestResultItem[] = result?.items ?? []
    const pagedItems = items.slice(dialogPage * PAGE_SIZE, (dialogPage + 1) * PAGE_SIZE)
    const totalPages = Math.ceil(items.length / PAGE_SIZE)

    return (
        <Card data-help-prefix="testRule" data-testid="test-results-panel">
            <CardHeader>
                <CardTitle className="text-base" data-help-key="_section" data-testid="card-title-test-results">
                    <IconText icon={FlaskConical} size="sm">
                        {i18n.restrictionRules.testRuleResults}
                    </IconText>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {isLoading ? (
                    <MutedText size="sm" data-testid="test-results-loading">
                        {i18n.restrictionRules.testRuleLoading}
                    </MutedText>
                ) : isError ? (
                    <MutedText size="sm" data-testid="test-results-error">
                        {i18n.restrictionRules.testRuleError}
                    </MutedText>
                ) : result && result.totalEvaluated === 0 ? (
                    <MutedText size="sm" data-testid="test-results-empty">
                        {i18n.restrictionRules.testRuleEmpty}
                    </MutedText>
                ) : result ? (
                    <>
                        <div className="flex gap-6 mb-4">
                            <div>
                                <MutedText size="xs" uppercase>
                                    {i18n.restrictionRules.evaluated}
                                </MutedText>
                                <p className="text-2xl font-semibold" data-testid="test-results-evaluated">
                                    {result.totalEvaluated}
                                </p>
                            </div>
                            <div>
                                <MutedText size="xs" uppercase>
                                    {i18n.restrictionRules.matched}
                                </MutedText>
                                <p className="text-2xl font-semibold" data-testid="test-results-matched">
                                    {result.matched}
                                </p>
                            </div>
                            <div>
                                <MutedText size="xs" uppercase>
                                    {i18n.restrictionRules.testViolations}
                                </MutedText>
                                <p className="text-2xl font-semibold text-destructive" data-testid="test-results-violations">
                                    {result.violations}
                                </p>
                            </div>
                        </div>
                        {items.length > 0 && (
                            <Button
                                variant="outline"
                                data-testid="btn-view-affected"
                                onClick={() => {
                                    setDialogPage(0)
                                    setDialogOpen(true)
                                }}
                            >
                                {i18n.restrictionRules.viewAffectedIdentities}
                            </Button>
                        )}
                        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                            <DialogContent data-testid="affected-identities-dialog" className="max-w-3xl">
                                <DialogHeader>
                                    <DialogTitle>{i18n.restrictionRules.affectedIdentitiesTitle}</DialogTitle>
                                    <DialogDescription>{i18n.restrictionRules.affectedIdentitiesDesc}</DialogDescription>
                                </DialogHeader>
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                                            <TableHead data-testid="col-party">{i18n.restrictionRules.party}</TableHead>
                                            <TableHead data-testid="col-violation-indicator">{i18n.restrictionRules.violationIndicator}</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {pagedItems.map((item) => (
                                            <TableRow key={item.identity.id}>
                                                <TableCell>
                                                    <EntityLink value={item.identity} testId={`link-affected-identity-${item.identity.id}`}/>
                                                </TableCell>
                                                <TableCell>
                                                    <EntityLink value={item.party} testId={`link-affected-party-${item.party.id}`}/>
                                                </TableCell>
                                                <TableCell>
                                                    {item.violation && (
                                                        <Badge variant={BadgeVariant.DESTRUCTIVE}>
                                                            {i18n.restrictionRules.violationIndicator}
                                                        </Badge>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                                {items.length > PAGE_SIZE && (
                                    <Pagination
                                        page={dialogPage}
                                        pageSize={PAGE_SIZE}
                                        totalItems={items.length}
                                        totalPages={totalPages}
                                        onPageChange={setDialogPage}
                                        onPageSizeChange={() => {}}
                                        pageSizeOptions={[PAGE_SIZE]}
                                        compact
                                    />
                                )}
                            </DialogContent>
                        </Dialog>
                    </>
                ) : null}
            </CardContent>
        </Card>
    )
}
